import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Clock, PhoneCall, Star, MapPin, RefreshCw } from "lucide-react";
import { CustomerLeadTitle } from "@/components/CustomerLeadTitle";
import { labelFor } from "@/lib/services";
import { ReportConductDialog } from "@/components/ReportConductDialog";

const STATUS = {
  requested: { label: "Awaiting collector", cls: "bg-[#E9C46A]/30 text-[#1E3F33]", icon: Clock },
  contacted: { label: "Collector says they reached out", cls: "bg-[#2D6A4F]/15 text-[#2D6A4F]", icon: PhoneCall },
  confirmed: { label: "Contact confirmed", cls: "bg-[#1E3F33]/10 text-[#1E3F33]", icon: CheckCircle2 },
};

function ReviewForm({ leadId, token, collectorName, highlight, onDone }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reviews", { lead_id: leadId, rating, comment, token });
      toast.success("Review posted — thank you!");
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className={`mt-3 pt-3 space-y-2 rounded-xl ${highlight ? "ring-2 ring-[#CC5A3A]/60 bg-[#CC5A3A]/5 p-3 -m-1" : "border-t border-[#1E3F33]/10"}`} data-testid={`review-form-${leadId}`}>
      <p className="text-sm font-medium text-[#1E3F33]">Rate {collectorName || "this collector"}</p>
      <div className="flex items-center gap-1">
        {[1,2,3,4,5].map((n)=>(
          <button key={n} type="button" data-testid={`review-star-${leadId}-${n}`} onClick={()=>setRating(n)} className="p-0.5">
            <Star className={`w-6 h-6 ${n<=rating?"fill-[#CC5A3A] text-[#CC5A3A]":"text-[#8A9A86]"}`}/>
          </button>
        ))}
      </div>
      <textarea data-testid={`review-comment-${leadId}`} rows={2} className="input-soft" placeholder="Add a comment (optional)" value={comment} onChange={(e)=>setComment(e.target.value)}/>
      <button data-testid={`review-submit-${leadId}`} disabled={busy} className="btn-primary text-sm py-2">{busy?"Posting…":"Post review"}</button>
    </form>
  );
}

export default function MyRequest() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const reviewId = searchParams.get("review");
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const leadRefs = useRef({});

  const load = async () => {
    try {
      const { data } = await api.get(`/service-requests/track/${token}`);
      setData(data);
    } catch {
      setNotFound(true);
    }
  };
  useEffect(() => { load(); }, [token]);

  useEffect(() => {
    if (reviewId && data && leadRefs.current[reviewId]) {
      leadRefs.current[reviewId].scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [reviewId, data]);

  const confirm = async (leadId) => {
    try {
      await api.patch(`/leads/${leadId}/confirm`, null, { params: { token } });
      toast.success("Contact confirmed — you can now leave a review");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  if (notFound) return <div className="container-x py-20 text-center text-[#5C6B64]" data-testid="track-not-found">Request not found. Please check your link.</div>;
  if (!data) return <div className="container-x py-20 text-[#5C6B64]">Loading…</div>;

  const { request, leads } = data;

  return (
    <div className="container-x py-12 max-w-3xl" data-testid="my-request-page">
      <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#CC5A3A] mb-2">Your request</div>
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold">Track &amp; confirm contact</h1>
      <p className="text-[#5C6B64] mt-2">
        Sent to <span className="font-medium text-[#1E3F33]">{request.matched_collectors}</span> collector{request.matched_collectors===1?"":"s"} · {request.when_label}
      </p>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {(request.services||[]).map((s)=>(<span key={s} className="text-xs bg-[#F0EFEA] text-[#1E3F33] rounded-full px-3 py-1">{labelFor(s)}</span>))}
        {(request.zip_codes||[]).map((z)=>(<span key={z} className="text-xs bg-[#F0EFEA] text-[#1E3F33] rounded-full px-3 py-1 inline-flex items-center gap-1"><MapPin className="w-3 h-3"/>{z}</span>))}
      </div>

      {request.declined_count > 0 && !request.closed && (
        <div className="mt-5 rounded-xl border border-[#E9C46A]/50 bg-[#E9C46A]/15 p-4 flex items-start gap-3" data-testid="track-decline-notice">
          <RefreshCw className="w-5 h-5 text-[#CC5A3A] shrink-0 mt-0.5"/>
          <div>
            <p className="text-sm font-medium text-[#1E3F33]">Your search is still active</p>
            <p className="text-sm text-[#5C6B64] mt-0.5">
              {request.declined_count} collector{request.declined_count===1?"":"s"} couldn't take this request, so we've alerted others in your zip code. You'll hear from an available collector soon.
            </p>
          </div>
        </div>
      )}

      <div className="mt-8 space-y-4">
        {leads.length === 0 ? (
          <div className="card-soft text-[#5C6B64]" data-testid="track-no-collectors">No collectors matched your zip code yet.</div>
        ) : leads.map((l) => {
          const st = STATUS[l.status] || STATUS.requested;
          const Icon = st.icon;
          return (
            <div key={l.id} ref={(el) => { leadRefs.current[l.id] = el; }} className="card-soft" data-testid={`track-lead-${l.id}`}>
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <CustomerLeadTitle l={l} large />
                  <div className="mt-2">
                    <span className={`text-xs px-2 py-1 rounded-full inline-flex items-center gap-1 ${st.cls}`} data-testid={`track-status-${l.id}`}>
                      <Icon className="w-3 h-3"/> {st.label}
                    </span>
                  </div>
                </div>
              </div>

              {l.status === "requested" && (
                <p className="text-sm text-[#8A9A86] mt-3">Waiting for a collector to reach out. You'll see who they are and can confirm once they do.</p>
              )}

              {l.can_review && (
                <>
                  {l.status === "contacted" && (
                    <p className="text-sm text-[#5C6B64] mt-3">
                      This collector says they reached out. Leave a review below — this also confirms contact.
                      {" "}
                      <button data-testid={`confirm-contact-${l.id}`} onClick={()=>confirm(l.id)} className="text-[#CC5A3A] underline hover:no-underline">Just confirm without a review</button>
                    </p>
                  )}
                  <ReviewForm
                    leadId={l.id}
                    token={token}
                    collectorName={l.collector_business_name}
                    highlight={reviewId === l.id}
                    onDone={load}
                  />
                </>
              )}

              {l.reviewed && (
                <p className="text-sm text-[#2D6A4F] mt-3 inline-flex items-center gap-1.5"><Star className="w-4 h-4 fill-[#CC5A3A] text-[#CC5A3A]"/> Reviewed — thank you!</p>
              )}

              <div className="border-t border-[#1E3F33]/10 mt-3 pt-1">
                <ReportConductDialog leadId={l.id} token={token} collectorName={l.collector_business_name} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
